import React, { memo, useCallback, useMemo, useRef } from 'react'
import { Rnd, RndResizeCallback, RndDragCallback } from 'react-rnd'
import { Tooltip } from 'primereact/tooltip'

import { ScheduleEntry } from './types'
import {
    SECONDS_IN_A_DAY,
    MINUTES_IN_A_DAY,
    SNAP_INCREMENT_MINUTES,
    PIXELS_PER_MINUTE,
    MAX_DURATION_SECONDS,
    MIN_DURATION_MINUTES,
} from './constants'
import { formatTime } from './utils'

interface ScheduleBlockProps {
    schedule: ScheduleEntry
    onUpdate: (updatedSchedule: ScheduleEntry) => void
    onDragStart: () => void
    onDragEnd: () => void
    onContextMenu: (event: React.MouseEvent | React.TouchEvent) => void
    dayIndex: number
    schedules: ScheduleEntry[] // Add schedules prop
    customMessage?: string
    disabled?: boolean
    warning?: string | null
    customStyle?: React.CSSProperties
    formatCurrent?: (maxCurrent: number) => string
}

const gridSize = PIXELS_PER_MINUTE * SNAP_INCREMENT_MINUTES // e.g., 0.6667 * 15 = 10 pixels

const resizeGrid: [number, number] = [1, gridSize]
const dragGrid: [number, number] = [1, gridSize]

const ScheduleBlock: React.FC<ScheduleBlockProps> = memo(
    ({
        schedule,
        onUpdate,
        onDragStart,
        onDragEnd,
        onContextMenu,
        dayIndex,
        schedules,
        customMessage,
        disabled,
        warning,
        customStyle,
        formatCurrent,
    }) => {
        const isDisabled = disabled

        const segment = splitSchedule(schedule, dayIndex)

        const topPosition = segment ? calculateTopPosition(segment.startTimeInDay) : 0
        const segmentHeight = segment ? calculateHeight(segment.startTimeInDay, segment.endTimeInDay) : 0

        const size = useMemo(() => ({ width: '100%', height: segmentHeight }), [segmentHeight])
        const position = { x: 0, y: topPosition }

        const longPressTimeoutRef = useRef<number | null>(null)
        const initialTouchPositionRef = useRef<{ x: number; y: number } | null>(null)

        const enableResizing = useMemo(() => {
            if (isDisabled) {
                return false
            }
            return { top: true, bottom: true }
        }, [isDisabled])

        const handleDragStop: RndDragCallback = useCallback(
            (e, d) => {
                const deltaY = d.y - topPosition
                const deltaMinutes = deltaY / PIXELS_PER_MINUTE
                const deltaSeconds = deltaMinutes * 60

                let newFrom = schedule.from + deltaSeconds
                let newTo = schedule.to + deltaSeconds

                // Snap to grid
                newFrom = snapToGrid(newFrom)
                newTo = snapToGrid(newTo)

                // Enforce minimum start time
                if (newFrom < 0) {
                    const duration = newTo - newFrom
                    newFrom = 0
                    newTo = newFrom + duration
                }

                // Enforce maximum duration
                const duration = newTo - newFrom
                if (duration > MAX_DURATION_SECONDS) {
                    // Adjust newTo to enforce maximum duration
                    newTo = newFrom + MAX_DURATION_SECONDS
                }

                // Handle overlaps
                if (isOverlapping(newFrom, newTo, schedule.id, schedules)) {
                    // Revert to original times if overlapping
                    newFrom = schedule.from
                    newTo = schedule.to
                }

                onUpdate({ ...schedule, from: newFrom, to: newTo })
                onDragEnd()
            },
            [onDragEnd, onUpdate, schedule, schedules, topPosition]
        )

        const handleResizeStop: RndResizeCallback = useCallback(
            (e, direction, ref, delta, position) => {
                let newFrom = schedule.from
                let newTo = schedule.to

                if (direction === 'top') {
                    const deltaY = position.y - topPosition
                    const deltaMinutes = deltaY / PIXELS_PER_MINUTE
                    const deltaSeconds = deltaMinutes * 60
                    newFrom = schedule.from + deltaSeconds
                } else if (direction === 'bottom') {
                    const deltaHeight = ref.offsetHeight - segmentHeight
                    const deltaMinutes = deltaHeight / PIXELS_PER_MINUTE
                    const deltaSeconds = deltaMinutes * 60
                    newTo = schedule.to + deltaSeconds
                }

                // Snap to grid
                newFrom = snapToGrid(newFrom)
                newTo = snapToGrid(newTo)

                // Enforce minimum duration
                const minDurationSeconds = MIN_DURATION_MINUTES * 60
                if (newTo - newFrom < minDurationSeconds) {
                    if (direction === 'top') {
                        newFrom = newTo - minDurationSeconds
                    } else {
                        newTo = newFrom + minDurationSeconds
                    }
                }

                // Enforce maximum duration
                const duration = newTo - newFrom
                if (duration > MAX_DURATION_SECONDS) {
                    if (direction === 'top') {
                        newFrom = newTo - MAX_DURATION_SECONDS
                    } else if (direction === 'bottom') {
                        newTo = newFrom + MAX_DURATION_SECONDS
                    }
                }

                // Enforce minimum start time
                if (newFrom < 0) {
                    newFrom = 0
                }

                // Handle overlaps
                if (isOverlapping(newFrom, newTo, schedule.id, schedules)) {
                    // Revert to original times if overlapping
                    newFrom = schedule.from
                    newTo = schedule.to
                }

                onUpdate({ ...schedule, from: newFrom, to: newTo })
                onDragEnd()
            },
            [onDragEnd, onUpdate, schedule, schedules, segmentHeight, topPosition]
        )

        const handleContextMenuEvent = useCallback(
            (e: React.MouseEvent | React.TouchEvent) => {
                e.preventDefault()
                e.stopPropagation()
                if (onContextMenu) {
                    onContextMenu(e)
                }
            },
            [onContextMenu]
        )

        // Desktop right-click
        const handleContextMenu = useCallback(
            (e: React.MouseEvent | React.TouchEvent) => {
                handleContextMenuEvent(e)
            },
            [handleContextMenuEvent]
        )

        const handleTouchStart = useCallback(
            (e: React.TouchEvent) => {
                initialTouchPositionRef.current = {
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY,
                }
                longPressTimeoutRef.current = window.setTimeout(() => {
                    handleContextMenuEvent(e)
                }, 600) // Adjust the duration as needed
            },
            [handleContextMenuEvent]
        )

        const handleTouchMove = useCallback((e: React.TouchEvent) => {
            if (initialTouchPositionRef.current) {
                const deltaX = e.touches[0].clientX - initialTouchPositionRef.current.x
                const deltaY = e.touches[0].clientY - initialTouchPositionRef.current.y
                const moveThreshold = 10 // pixels

                if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
                    // Cancel the long-press timer
                    if (longPressTimeoutRef.current) {
                        clearTimeout(longPressTimeoutRef.current)
                        longPressTimeoutRef.current = null
                    }
                }
            }
        }, [])

        const handleTouchEnd = useCallback(() => {
            if (longPressTimeoutRef.current) {
                clearTimeout(longPressTimeoutRef.current)
                longPressTimeoutRef.current = null
            }
        }, [])

        const handleDragStart = useCallback(
            (e, data) => {
                // Cancel long-press timer
                if (longPressTimeoutRef.current) {
                    clearTimeout(longPressTimeoutRef.current)
                    longPressTimeoutRef.current = null
                }
                onDragStart()
            },
            [onDragStart]
        )

        const handleResizeStart = useCallback(
            (e, dir, ref) => {
                // Cancel long-press timer
                if (longPressTimeoutRef.current) {
                    clearTimeout(longPressTimeoutRef.current)
                    longPressTimeoutRef.current = null
                }
                onDragStart()
            },
            [onDragStart]
        )

        if (!segment) return null

        const timeRange = `${formatTime(schedule.from)} - ${formatTime(schedule.to)}`

        const resizeHandles = isDisabled
            ? {}
            : {
                  top: (
                      <div
                          className="custom-resize-handle custom-resize-handle-top react-resizable-handle"
                          data-direction="top"
                      />
                  ),
                  bottom: (
                      <div
                          className="custom-resize-handle custom-resize-handle-bottom react-resizable-handle"
                          data-direction="bottom"
                      />
                  ),
              }

        return (
            <Rnd
                className="schedule-segment"
                size={size}
                position={position}
                onResizeStart={handleResizeStart}
                onResizeStop={handleResizeStop}
                onDragStart={handleDragStart}
                onDragStop={handleDragStop}
                bounds="parent"
                enableResizing={enableResizing}
                disableDragging={isDisabled}
                dragAxis="y"
                resizeGrid={resizeGrid}
                dragGrid={dragGrid}
                enableUserSelectHack={false}
                allowAnyClick={true}
                resizeHandleComponent={resizeHandles}
                cancel=".react-resizable-handle"
            >
                <div
                    className="schedule-block"
                    onContextMenu={handleContextMenu}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="schedule-content" style={customStyle}>
                        <div className="flex gap-2 align-items-center justify-content-between">
                            {timeRange}

                            {warning ? (
                                <>
                                    <Tooltip target=".schedule-tooltip-target" />
                                    <i
                                        style={{ color: 'var(--text-color-info)' }}
                                        className="schedule-tooltip-target pi pi-info-circle cursor-pointer"
                                        data-pr-tooltip={warning}
                                        data-pr-position="top"
                                    ></i>
                                </>
                            ) : null}
                        </div>
                        <div className="text-sm text-left mb-1" style={{ fontFamily: 'var(--font-family-header)' }}>
                            {typeof formatCurrent === 'function'
                                ? formatCurrent(schedule.maxCurrent)
                                : `${schedule.maxCurrent}A`}
                        </div>
                        {customMessage && (
                            <>
                                <div className="text-xs text-left text-color-secondary">{customMessage}</div>
                            </>
                        )}
                    </div>
                </div>
            </Rnd>
        )
    }
)

export default ScheduleBlock

// Helper Functions

interface ScheduleSegment {
    dayIndex: number
    startTimeInDay: number
    endTimeInDay: number
}

const splitSchedule = (schedule: ScheduleEntry, dayIndex: number): ScheduleSegment | null => {
    const dayStart = dayIndex * SECONDS_IN_A_DAY
    const dayEnd = (dayIndex + 1) * SECONDS_IN_A_DAY

    const scheduleStart = Math.max(schedule.from, dayStart)
    const scheduleEnd = Math.min(schedule.to, dayEnd)

    if (scheduleStart >= scheduleEnd) {
        return null // Schedule does not overlap with this day
    }

    return {
        dayIndex,
        startTimeInDay: scheduleStart - dayStart,
        endTimeInDay: scheduleEnd - dayStart,
    }
}

const calculateTopPosition = (startTimeInDay: number): number => {
    const minutesFromMidnight = startTimeInDay / 60
    return minutesFromMidnight * PIXELS_PER_MINUTE
}

const calculateHeight = (startTimeInDay: number, endTimeInDay: number): number => {
    let durationInMinutes = (endTimeInDay - startTimeInDay) / 60
    if (durationInMinutes <= 0) {
        durationInMinutes += MINUTES_IN_A_DAY // Add 24 hours in minutes
    }
    return durationInMinutes * PIXELS_PER_MINUTE
}

const snapToGrid = (seconds: number): number => {
    const minutes = seconds / 60
    const snappedMinutes = Math.round(minutes / SNAP_INCREMENT_MINUTES) * SNAP_INCREMENT_MINUTES
    return snappedMinutes * 60 // Convert back to seconds
}

const isOverlapping = (newFrom: number, newTo: number, scheduleId: string, schedules: ScheduleEntry[]): boolean => {
    return schedules.some((s) => s.id !== scheduleId && Math.max(s.from, newFrom) < Math.min(s.to, newTo))
}
